"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LeadWonHookSchema = exports.LeadInternalEventSchema = exports.RubyDraftRequestSchema = exports.LeadEmailSchema = exports.LeadNoteSchema = exports.LeadStageMoveSchema = exports.RejectLeadSchema = exports.AssignLeadSchema = exports.ApproveLeadSchema = exports.LeadActivitySchema = exports.LeadSchema = exports.LeadWonPlanSchema = exports.ManualLeadSchema = exports.LeadRequestSchema = exports.LEAD_ACTOR_TYPES = exports.LEAD_ACTIVITY_TYPES = exports.businessTypeLabel = exports.BUSINESS_TYPES = exports.BUSINESS_TYPE_IDS = exports.LEAD_EMAIL_TONES = exports.LEAD_EMAIL_PURPOSES = exports.LEAD_CALL_OUTCOMES = exports.LEAD_LOST_REASONS = exports.LEAD_REJECT_REASONS = exports.LEAD_TIMES = exports.LEAD_DAYS = exports.LEAD_LOCATIONS_BANDS = exports.LEAD_INTENTS = exports.LEAD_OPEN_STAGES = exports.LEAD_SYSTEM_STAGES = exports.LEAD_LEGACY_STATUSES = exports.LeadStageSchema = exports.LEAD_STAGES = void 0;
exports.stageFromLegacyStatus = stageFromLegacyStatus;
exports.legacyStatusForStage = legacyStatusForStage;
exports.effectiveStage = effectiveStage;
/**
 * LEADS DESK — sales enquiries from rubytill.com, worked in Super Admin →
 * Command Center → Leads. Spec: docs/superpowers/specs/2026-10-11-leads-desk-design.md
 *
 * `stage` is the pipeline. `status` is the LEGACY projection and is ALWAYS
 * written alongside it (legacyStatusForStage), so every existing reader of
 * `status` keeps working (§2). A row with no `stage` is read through
 * stageFromLegacyStatus — existing leads predate the screening gate, so they
 * never land in `review`.
 */
const zod_1 = require("zod");
exports.LEAD_STAGES = ['review', 'new', 'contacted', 'demo_booked', 'deal_sent', 'signed_up', 'won', 'lost', 'rejected'];
exports.LeadStageSchema = zod_1.z.enum(exports.LEAD_STAGES);
exports.LEAD_LEGACY_STATUSES = ['new', 'contacted', 'onboarded', 'closed'];
/** Only the system sets these (deals-service signup hook, billing-service won hook). */
exports.LEAD_SYSTEM_STAGES = ['signed_up', 'won'];
/** Stages a lead is still being worked in. */
exports.LEAD_OPEN_STAGES = ['review', 'new', 'contacted', 'demo_booked', 'deal_sent', 'signed_up'];
exports.LEAD_INTENTS = ['demo', 'call', 'question', 'manual'];
exports.LEAD_LOCATIONS_BANDS = ['1', '2-3', '4-10', '10+'];
exports.LEAD_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
exports.LEAD_TIMES = ['morning', 'afternoon', 'evening'];
exports.LEAD_REJECT_REASONS = ['spam', 'duplicate', 'not_a_fit', 'other'];
exports.LEAD_LOST_REASONS = ['no_response', 'chose_competitor', 'price', 'timing', 'not_a_fit', 'other'];
exports.LEAD_CALL_OUTCOMES = ['reached', 'voicemail', 'no_answer'];
exports.LEAD_EMAIL_PURPOSES = ['first_reply', 'confirm_demo', 'after_demo', 'follow_up', 'finish_signup', 'win_back'];
exports.LEAD_EMAIL_TONES = ['warm', 'brief', 'formal'];
/**
 * The new-business wizard's business types (platform-app
 * components/onboarding/StepBusiness.tsx:19 keeps its icons and should take
 * ids/labels from here). ONE list, so the request form, the desk and the
 * wizard never drift (spec §3.1 "uses the same list as the new-business wizard").
 */
exports.BUSINESS_TYPE_IDS = ['vape_smoke', 'convenience', 'liquor', 'grocery', 'restaurant', 'services', 'retail', 'other'];
exports.BUSINESS_TYPES = [
    { id: 'vape_smoke', label: 'Vape & Smoke', hint: 'Age-checked retail' },
    { id: 'convenience', label: 'Convenience', hint: 'C-store or gas' },
    { id: 'liquor', label: 'Liquor', hint: 'Beer, wine, spirits' },
    { id: 'grocery', label: 'Grocery', hint: 'Market or deli' },
    { id: 'restaurant', label: 'Food & Drink', hint: 'Restaurant or café' },
    { id: 'services', label: 'Services', hint: 'Salon, repair, studio' },
    { id: 'retail', label: 'General Retail', hint: 'Anything else on a shelf' },
    { id: 'other', label: 'Something else', hint: 'Tell us during setup' },
];
const businessTypeLabel = (id) => exports.BUSINESS_TYPES.find((b) => b.id === id)?.label ?? (typeof id === 'string' ? id : '');
exports.businessTypeLabel = businessTypeLabel;
/** §3.2 + §3.3 (`reminder_scheduled`) + `unsubscribed` (the sales-email opt-out, §4.5). */
exports.LEAD_ACTIVITY_TYPES = [
    'created', 'approved', 'rejected', 'assigned', 'stage_changed', 'note', 'call_logged',
    'email_sent', 'email_scheduled', 'email_cancelled', 'reminder_scheduled',
    'deal_sent', 'offer_opened', 'signed_up', 'won', 'reopened', 'ruby_coach', 'unsubscribed',
];
exports.LEAD_ACTOR_TYPES = ['staff', 'system', 'website'];
function stageFromLegacyStatus(status) {
    switch (status) {
        case 'contacted': return 'contacted';
        case 'onboarded': return 'signed_up';
        case 'closed': return 'lost';
        default: return 'new';
    }
}
function legacyStatusForStage(stage) {
    switch (stage) {
        case 'review':
        case 'new':
            return 'new';
        case 'contacted':
        case 'demo_booked':
        case 'deal_sent':
            return 'contacted';
        case 'signed_up':
        case 'won':
            return 'onboarded';
        case 'lost':
        case 'rejected':
            return 'closed';
    }
}
/** The stage to show for any stored row — its own `stage`, else the legacy mapping. */
function effectiveStage(row) {
    const s = exports.LeadStageSchema.safeParse(row?.stage);
    return s.success ? s.data : stageFromLegacyStatus(row?.status);
}
const IANA_TZ = /^(UTC|[A-Za-z]+(?:\/[A-Za-z0-9_+\-]+){1,2})$/;
const text = (max) => zod_1.z.string().trim().max(max);
const required = (max) => zod_1.z.string().trim().min(1).max(max);
const emailField = zod_1.z.string().trim().max(254).email().transform((s) => s.toLowerCase());
/** What the rubytill.com request form posts. Every field beyond the original four is optional. */
exports.LeadRequestSchema = zod_1.z.object({
    name: required(120),
    email: emailField,
    businessName: required(160),
    phone: text(40).optional(),
    intent: zod_1.z.enum(['demo', 'call', 'question']).optional(),
    businessType: zod_1.z.enum(exports.BUSINESS_TYPE_IDS).optional(),
    locationsBand: zod_1.z.enum(exports.LEAD_LOCATIONS_BANDS).optional(),
    preferredDays: zod_1.z.array(zod_1.z.enum(exports.LEAD_DAYS)).max(7).optional(),
    preferredTimes: zod_1.z.array(zod_1.z.enum(exports.LEAD_TIMES)).max(3).optional(),
    timezone: zod_1.z.string().trim().max(64).regex(IANA_TZ).optional(),
    message: text(2000).optional(),
    sourcePage: text(40).optional(),
    planInterest: text(80).optional(),
    referralCode: text(64).optional(),
    /** Honeypot — real visitors never fill it. The handler drops any request that does. */
    website: zod_1.z.string().max(500).optional(),
});
/** POST /leads/manual — a staff member typed it in, so it skips Review. */
exports.ManualLeadSchema = exports.LeadRequestSchema.omit({ website: true, intent: true }).extend({
    ownerStaffId: zod_1.z.string().min(1).max(128).optional(),
});
exports.LeadWonPlanSchema = zod_1.z.object({
    packageName: zod_1.z.string().max(200),
    /** Monthly total in INTEGER CENTS. */
    monthly: zod_1.z.number().int().nonnegative(),
    locations: zod_1.z.number().int().nonnegative(),
    addOns: zod_1.z.array(zod_1.z.string().max(200)).max(100),
});
/** A stored lead. Every Leads Desk field is optional; unknown legacy fields pass through. */
exports.LeadSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    name: zod_1.z.string().optional(),
    email: zod_1.z.string().optional(),
    businessName: zod_1.z.string().optional(),
    phone: zod_1.z.string().optional(),
    /** Legacy projection of `stage`. */
    status: zod_1.z.string().optional(),
    submittedAt: zod_1.z.number().optional(),
    createdAt: zod_1.z.number().optional(),
    affiliateId: zod_1.z.string().optional(),
    referralCode: zod_1.z.string().optional(),
    isAffiliateTrial: zod_1.z.boolean().optional(),
    trialCodeId: zod_1.z.string().optional(),
    stage: exports.LeadStageSchema.optional(),
    intent: zod_1.z.enum(exports.LEAD_INTENTS).optional(),
    businessType: zod_1.z.string().optional(),
    locationsBand: zod_1.z.enum(exports.LEAD_LOCATIONS_BANDS).optional(),
    preferredDays: zod_1.z.array(zod_1.z.enum(exports.LEAD_DAYS)).optional(),
    preferredTimes: zod_1.z.array(zod_1.z.enum(exports.LEAD_TIMES)).optional(),
    timezone: zod_1.z.string().optional(),
    message: zod_1.z.string().optional(),
    sourcePage: zod_1.z.string().optional(),
    planInterest: zod_1.z.string().optional(),
    ownerStaffId: zod_1.z.string().optional(),
    ownerName: zod_1.z.string().optional(),
    /** Denormalised so demo reminders and won emails reach the owner without a lookup. */
    ownerEmail: zod_1.z.string().optional(),
    approvedAt: zod_1.z.number().optional(),
    approvedBy: zod_1.z.string().optional(),
    rejectReason: zod_1.z.enum(exports.LEAD_REJECT_REASONS).optional(),
    rejectNote: zod_1.z.string().optional(),
    lostReason: zod_1.z.enum(exports.LEAD_LOST_REASONS).optional(),
    lostNote: zod_1.z.string().optional(),
    demoAt: zod_1.z.number().optional(),
    demoTz: zod_1.z.string().optional(),
    dealInviteId: zod_1.z.string().optional(),
    dealCode: zod_1.z.string().optional(),
    dealSentAt: zod_1.z.number().optional(),
    offerOpens: zod_1.z.number().int().nonnegative().optional(),
    /** Exists today (deals-service). Older rows may hold null. */
    onboardedTenantId: zod_1.z.string().nullable().optional(),
    signedUpAt: zod_1.z.number().optional(),
    wonAt: zod_1.z.number().optional(),
    wonPlan: exports.LeadWonPlanSchema.optional(),
    lastActivityAt: zod_1.z.number().optional(),
    /** Events written for this lead — drives the rolling Ruby summary refresh. */
    eventCount: zod_1.z.number().int().nonnegative().optional(),
    rubyReview: zod_1.z.object({
        text: zod_1.z.string(),
        at: zod_1.z.number(),
        fit: zod_1.z.enum(['strong', 'possible', 'weak']).optional(),
        plan: zod_1.z.string().optional(),
        /** true = "Looks like spam" on the Review card. */
        spam: zod_1.z.boolean().optional(),
    }).optional(),
    rubySummary: zod_1.z.object({ text: zod_1.z.string(), at: zod_1.z.number(), eventCount: zod_1.z.number().int().nonnegative() }).optional(),
    /** Prospect unsubscribed from sales email (§4.5). Blocks sends with a 409. */
    doNotEmail: zod_1.z.boolean().optional(),
    doNotEmailAt: zod_1.z.number().optional(),
    updatedAt: zod_1.z.number().optional(),
}).passthrough();
/** One append-only timeline row (§3.2). pk leadId, sk `<13-digit at>#<eventId>`. */
exports.LeadActivitySchema = zod_1.z.object({
    leadId: zod_1.z.string().min(1),
    sk: zod_1.z.string().regex(/^\d{13}#.+$/),
    eventId: zod_1.z.string().min(1),
    at: zod_1.z.number().int().nonnegative(),
    type: zod_1.z.enum(exports.LEAD_ACTIVITY_TYPES),
    actorType: zod_1.z.enum(exports.LEAD_ACTOR_TYPES),
    actorId: zod_1.z.string().optional(),
    actorName: zod_1.z.string().optional(),
    data: zod_1.z.record(zod_1.z.unknown()).default({}),
    /** Scheduled rows only: 'pending' while waiting; REMOVED when sent or cancelled. */
    dueBucket: zod_1.z.literal('pending').optional(),
    dueAt: zod_1.z.number().int().optional(),
});
// ── Admin request bodies (leads-service; Plan B posts these) ────────────────
exports.ApproveLeadSchema = zod_1.z.object({ ownerStaffId: zod_1.z.string().min(1).max(128) });
exports.AssignLeadSchema = zod_1.z.object({ ownerStaffId: zod_1.z.string().min(1).max(128) });
exports.RejectLeadSchema = zod_1.z.object({ reason: zod_1.z.enum(exports.LEAD_REJECT_REASONS), note: text(1000).optional() });
exports.LeadStageMoveSchema = zod_1.z.object({
    stage: exports.LeadStageSchema,
    demoAt: zod_1.z.number().int().positive().optional(),
    demoTz: zod_1.z.string().trim().max(64).regex(IANA_TZ).optional(),
    lostReason: zod_1.z.enum(exports.LEAD_LOST_REASONS).optional(),
    note: text(1000).optional(),
});
exports.LeadNoteSchema = zod_1.z.object({
    text: required(4000),
    kind: zod_1.z.enum(['note', 'call']).default('note'),
    outcome: zod_1.z.enum(exports.LEAD_CALL_OUTCOMES).optional(),
    durationMin: zod_1.z.number().int().min(0).max(600).optional(),
});
exports.LeadEmailSchema = zod_1.z.object({
    subject: required(200),
    html: zod_1.z.string().min(1).max(100000),
    text: zod_1.z.string().min(1).max(50000),
    /** Epoch ms. Absent or within the next minute = send now. */
    sendAt: zod_1.z.number().int().positive().optional(),
    rubyDrafted: zod_1.z.boolean().default(false),
});
exports.RubyDraftRequestSchema = zod_1.z.object({
    purpose: zod_1.z.enum(exports.LEAD_EMAIL_PURPOSES),
    tone: zod_1.z.enum(exports.LEAD_EMAIL_TONES).default('warm'),
    steer: text(500).optional(),
});
// ── Internal hooks (deals-service, billing-service) ─────────────────────────
exports.LeadInternalEventSchema = zod_1.z.object({
    type: zod_1.z.enum(['deal_sent', 'offer_opened', 'signed_up']),
    data: zod_1.z.record(zod_1.z.unknown()).default({}),
    stage: zod_1.z.enum(['deal_sent', 'signed_up']).optional(),
});
exports.LeadWonHookSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1).max(128),
    dealCode: zod_1.z.string().max(64).optional(),
    plan: exports.LeadWonPlanSchema,
});
//# sourceMappingURL=lead.schema.js.map